import { useEffect, useState } from 'react'
import { useForm, useWatch} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Divider,
  Checkbox,
  Tooltip,
} from '@chakra-ui/react'
import { createShippingLineSchema, updateShippingLineSchema, type CreateShippingLineFormData, type UpdateShippingLineFormData } from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { Form } from 'react-router-dom'

interface ShippingLineFormFormProps {
  shippingLineId?: number
  onSuccess: () => void
  onCancel: () => void
}

export function ShippingLineForm({ shippingLineId, onSuccess, onCancel }: ShippingLineFormFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!shippingLineId
  const { showError, showSuccess } = useToastNotification()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateShippingLineFormData | UpdateShippingLineFormData>({
    resolver: zodResolver(isEditMode ? updateShippingLineSchema : createShippingLineSchema),
    defaultValues: {
      name: '',
      short_name: '',
      email_address: '',
      address: '',
      contact_name: '',
      contact_mobile: '',
      fax_no: '',
      tin: '',
      landlines: [],
      shipping_lines_template: [],
      transaction_information_template: [],
    },
  })

  type SelectedItem = {
    parent_id: number
    name: string
    }
  const shippingTemplates = watch('shipping_lines_template') ?? []
  const transactionTemplates = watch('transaction_information_template') ?? []
  const [shippingLineDataOption, setShippingLineDataOption] = useState<{ id: number; parent_id: number; name: string }[]>([])
  const [transactionDataOption, setTransactionDataOption] = useState<{ id: number; parent_id: number; name: string }[]>([])

  useEffect(() => {
  const fetchData = async () => {
    try {
      // Always fetch Shipping Line Template Data Options
      const shippingLineDataResponse = await apiClient.get(`options/soa-data-options/parents/1/children`);
      const shippingLineData = shippingLineDataResponse.data?.data || shippingLineDataResponse.data || [];
      setShippingLineDataOption(shippingLineData);

      // Fetch Transaction Line Template Data Options
      const transactionDataResponse = await apiClient.get(`options/soa-data-options/parents/2/children`);
      const transactionData = transactionDataResponse.data?.data || transactionDataResponse.data || [];
      setTransactionDataOption(transactionData);

      // If edit mode, fetch shipping line details
      if (isEditMode && shippingLineId) {
        const response = await apiClient.get(`/shipping-lines/${shippingLineId}`);
        const shippingLine = response.data?.data || response.data || {};

        reset({
          name: shippingLine.name || '',
          short_name: shippingLine.short_name ?? '',
          email_address: shippingLine.email_address || '',
          address: shippingLine.address || '',
          contact_name: shippingLine.contact_name || '',
          contact_mobile: shippingLine.contact_mobile || '',
          fax_no: shippingLine.fax_no || '',
          tin: shippingLine.tin || '',
          landlines: Array.isArray(shippingLine.landlines)
            ? shippingLine.landlines.filter(Boolean)
            : typeof shippingLine.landlines === 'string'
            ? [shippingLine.landlines]  // convert single string to array
            : [],
          shipping_lines_template: shippingLine.shipping_lines_template ?? [],
          transaction_information_template: shippingLine.transaction_information_template ?? [],
        });

        // Insert Database Values into their Respective Checkboxes
        setValue('shipping_lines_template', shippingLine.shipping_lines_template ?? [])
        setValue('transaction_information_template', shippingLine.transaction_information_template ?? [])
      }
      else{
        // use the variables you JUST fetched
        const shippingIds = shippingLineData.map((item: any) => item.id)
        const transactionIds = transactionData.map((item: any) => item.id)

        setValue('shipping_lines_template', shippingIds)
        setValue('transaction_information_template', transactionIds)

      }
    } catch (error: any) {
      showError(error);
    }
  };
  fetchData();
}, [isEditMode, shippingLineId, reset, showError]);
  

  const onSubmit = async (data: CreateShippingLineFormData | UpdateShippingLineFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name.trim(),
        short_name: data.short_name != null && String(data.short_name).trim() !== '' ? String(data.short_name).trim() : null,
        email_address: data.email_address.trim(),
        address: data.address || '',
        contact_name: data.contact_name || '',
        contact_mobile: data.contact_mobile || '',
        fax_no: data.fax_no || '',
        tin: data.tin || '',
        // Accept either array or comma-separated string from the form and normalize to string[]
        landlines:
          Array.isArray((data as any).landlines) ? (data as any).landlines : typeof (data as any).landlines === 'string' ? 
          (data as any).landlines.split(',').map((s: string) => s.trim()).filter(Boolean) : [],  
        shipping_lines_template: data.shipping_lines_template ?? [],
        transaction_information_template: data.transaction_information_template ?? [],
      }

      if (isEditMode) {
        await apiClient.put(`/shipping-lines/${shippingLineId}`, payload)
        showSuccess('Success', 'Shipping Line updated successfully')
      } else {
        await apiClient.post('/shipping-lines', payload)
        showSuccess('Success', 'Shipping Line created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Shipping Line Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. ABC Company"/>
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Short Name
          </FormLabel>
          <FormControl isInvalid={!!errors.short_name}>
            <Input {...register('short_name')} placeholder="e.g. abc"/>
            <FormErrorMessage>{errors.short_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email Address <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.email_address}>
            <Input {...register('email_address')} placeholder="e.g. abc.company@gmail.com"/>
            <FormErrorMessage>{errors.email_address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Address <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.address}>
            <Input {...register('address')} placeholder="e.g. 123 Rizal Ave, Brgy. San Lorenzo, Makati City 1223"/>
            <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Contact Name <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_name}>
            <Input {...register('contact_name')} placeholder="e.g. John Doe,"/>
            <FormErrorMessage>{errors.contact_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Contact Mobile <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.contact_mobile}>
            <Input {...register('contact_mobile')} placeholder="e.g. +63-917-123-4567"/>
            <FormErrorMessage>{errors.contact_mobile?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Landline
          </FormLabel>
          <FormControl isInvalid={!!errors.landlines}>
            <Input {...register('landlines')} placeholder="e.g. +63-2-123-4567, +63-2-345-6789 (Separate Landline Numbers using a Comma)"/>
            <FormErrorMessage>{errors.landlines?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Fax No
          </FormLabel>
          <FormControl isInvalid={!!errors.fax_no}>
            <Input {...register('fax_no')} placeholder="e.g. +63-2-987-6543"/>
            <FormErrorMessage>{errors.fax_no?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Tin <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.tin}>
            <Input {...register('tin')} placeholder="e.g. 123-456-789-000"/>
            <FormErrorMessage>{errors.tin?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider/>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
            Shipping Line Data Option <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.shipping_lines_template}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            {shippingLineDataOption.map(item => (
              <Checkbox
                key={item.id}
                isChecked={shippingTemplates.includes(item.id)}
                onChange={(e) => {
                  const updated = e.target.checked
                  ? [...shippingTemplates, item.id]
                  : shippingTemplates.filter(id => id !== item.id)

                 setValue('shipping_lines_template', updated, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              >
                {item.name}
              </Checkbox>
            ))}
          </Grid>
          <FormErrorMessage>{errors.shipping_lines_template?.message}</FormErrorMessage>
          </FormControl>
        </Grid>
      
        <Divider/>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
            Statement of Account Data Option <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.transaction_information_template}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            {transactionDataOption.map(item => (
              <Checkbox
                key={item.id}
                isChecked={transactionTemplates.includes(item.id)}
                onChange={(e) => {
                  const updated = e.target.checked
                  ? [...transactionTemplates, item.id]
                  : transactionTemplates.filter(id => id !== item.id)

                 setValue('transaction_information_template', updated, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              >
                {item.name}
              </Checkbox>
            ))}
          </Grid>
          <FormErrorMessage>{errors.transaction_information_template?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider/>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Shipping Line'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}